import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { swal, typeChecker } from "../../../utils/methods";
import { _post } from "../../../utils/network";

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from "../../../components/Form/FormField/FormField.component";
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';

const ConsultationsCreate = ({ componentType='create' }) => {
	const { t } = useTranslation();
	const { id } = useParams();
	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('consultations'), link: '/consultations-list' },
		{ title: t(componentType) }
	];
	const confirmSwal = statements => {
		swal(() => statements(), {
			text: t('are_you_sure_to_do_this'),
			confirmButtonText: t('yes'),
			cancelButtonText: t('no')
		});
	};

	const [createButtonIsLoading, setCreateButtonIsLoading] = useState(false);
	const createConsultation = async (event) => {
		event.preventDefault();
		if(createButtonIsLoading)
			return 0;
		let adminAnswer = event.target.consultationAdminAnswer.value.trim();
		let adminFile = document.querySelector('#consultationAdminFile-upload-list a')?.getAttribute('href').trim();
		let status = event.target.consultationStatus.value;
		let body = {
			consultation_id: id,
			admin_answer_consultation_information: {
				admin_answer: adminAnswer,
				admin_attachment: adminFile
			},
			consultation_state: status
		};
		confirmSwal(async () => {
			setCreateButtonIsLoading(true);
			let response = await _post('/consultations/admin/' + componentType, body);
			setCreateButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					if(componentType === 'create')
						event.target.reset();
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	}
	const [consultation, setConsultation] = useState(null);
	const getConsultation = async () => {
		let body = {
			consultation_id: id
		};
		let response = await _post('/consultations/admin/getOne', body);
		switch(response.code){
			case 200:
				setConsultation(response.data.consultation);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const [updateCommentButtonIsLoading, setUpdateCommentButtonIsLoading] = useState(false);
	const updateUserComment = async (event) => {
		event.preventDefault();
		if(updateCommentButtonIsLoading)
			return 0;
		let userComment = event.target.consultationComment.value.trim();
		if(!typeChecker(userComment, 'title'))
			return toast.warn(t('user_comment') + ' ' + t('must_have_at_least_3_characters'));
		let userRate = event.target.consultationRate.value.trim();
		if(!typeChecker(userRate, 'rate'))
			return toast.warn(t('rate') + ' ' + t('must_have_between_0_and_5'));
		let status = event.target.consultationCommentStatus.value;
		let body = {
			consultation_id: id,
			user_comment: userComment,
			comment_status: status,
			user_rate: userRate
		};
		confirmSwal(async () => {
			setUpdateCommentButtonIsLoading(true);
			let response = await _post('/consultations/admin/editComment', body);
			setUpdateCommentButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	}

	const consultationStatusList = [
		{ key: t('pending'), value: 0 },
		{ key: t('canceled'), value: 30 },
		{ key: t('confirmed'), value: 1 },
		{ key: t('failed'), value: 10 },
		{ key: t('closed'), value: 20 }
	];

	const consultationCommentStatusList = [
		{ key: t('not_sent'), value: 0 },
		{ key: t('pending'), value: 1 },
		{ key: t('confirmed'), value: 2 },
		{ key: t('failed'), value: 3 }
	];
	
	useEffect(() => {
		if(componentType === 'edit')
			getConsultation();
	}, []);

	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t(componentType)} {t('consultation')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={createConsultation}>
						<div className="row gx-2">
							<FormField type="text" fieldKey="consultationDescription" title={t('description')} value={consultation?.consultation_information?.description} disabled rtl />
							<FormField type="file" fieldKey="consultationFile" title={t('file')} value={consultation?.consultation_information?.file} disabled fileMaxSize={2} />
							<FormField type="text" fieldKey="consultationAdminAnswer" title={t('admin_answer')} value={consultation?.admin_answer_consultation_information?.admin_answer} rtl />
							<FormField type="file" fieldKey="consultationAdminFile" title={t('admin_file')} value={consultation?.admin_answer_consultation_information?.admin_attachment} fileMaxSize={2} />
							<FormField fieldKey="consultationConsultant" title={t('consultant')} value={consultation?.consultant_id_information?.consultant_name} disabled rtl />
							<FormField fieldKey="consultationType" title={t('type')} value={consultation?.consultation_information?.type === 'online' ? 'آنلاین' : 'حضوری'} disabled rtl />
							<FormField fieldKey="consultationPrice" title={t('price')} value={consultation?.consultation_price} disabled ltr />
							<FormField type="select" fieldKey="consultationStatus" title={t('status')} value={consultation?.consultation_state} required selectItems={consultationStatusList} />
						</div>
						<div>
							{componentType === 'create' ?
								<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create" isLoading={createButtonIsLoading}>
									<i className="fa fa-save"></i> {t('create')}
								</LoadingButton>
							:
								<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="edit" isLoading={createButtonIsLoading}>
									<i className="fa fa-save"></i> {t('update')}
								</LoadingButton>
							}
						</div>
					</form>
				</div>
			</div>
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t('user_comment')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={updateUserComment}>
						<div className="row gx-2">
							<FormField type="text" fieldKey="consultationComment" title={t('user_comment')} value={consultation?.user_comment} required rtl />
							<FormField fieldKey="consultationRate" title={t('rate')} value={consultation?.user_rate} required ltr />
							<FormField type="select" fieldKey="consultationCommentStatus" title={t('comment_status')} value={consultation?.comment_status} required selectItems={consultationCommentStatusList} />
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="edit" isLoading={updateCommentButtonIsLoading}>
								<i className="fa fa-save"></i> {t('update')}
							</LoadingButton>
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default ConsultationsCreate;
